/** @file Creates a socket (grip axis a→b on a bone) with its socket bone and optional grip-2 bone. */
import { z } from 'zod';
import { ids } from '@pose2d/format/ids';
import { prim } from '@pose2d/format/doc/schemaParts/primitives';
import { throwError } from '@pose2d/format/throwError';
import type { Id } from '@pose2d/format/types';
import { defineCommand } from '../defineCommand';
import type { CommandContext } from '../commandContext';
import { defineCreate } from '../_shared/defineCreate';
import { insertSocketBones } from './insertSocketBones';

/** A client-supplied bone id when well-formed and unused (VALIDATION / ID_TAKEN at `path`), else a minted one. */
function boneId(ctx: CommandContext, given: string | undefined, path: string): Id {
  if (given === undefined) return ctx.newId('bone');
  if (!ids.re('bone').test(given)) throwError('VALIDATION', undefined, { path, hint: 'expected a bone_ id' });
  if (ctx.index.kindOf.has(given)) throwError('ID_TAKEN', { id: given }, { path });
  return given;
}

const create = defineCreate({
  domain: 'socket',
  kind: 'socket',
  description:
    'Create a socket (name at most 58 characters) on `parentBone`: grip axis a (base) → b (toward the tip) in that bone space; also creates the locked socket bone of the same name (origin a, X axis toward b, length |b - a|) and, with grip2: true, the `<name>-grip2` bone; boneId/grip2BoneId pre-mint their ids.',
  schema: z
    .object({
      id: z.string().exactOptional(),
      name: z.string().min(1),
      parentBone: z.string().min(1),
      a: prim.px,
      b: prim.px,
      grip2: z.boolean().default(false),
      boneId: z.string().exactOptional(),
      grip2BoneId: z.string().exactOptional(),
    })
    .strict(),
  build(input, ctx) {
    const bone = boneId(ctx, input.boneId, 'boneId');
    const socket = { name: input.name, parentBone: ctx.resolve('bone', input.parentBone), bone, a: input.a, b: input.b };
    if (!input.grip2) return socket;
    const grip2Bone = boneId(ctx, input.grip2BoneId, 'grip2BoneId');
    if (grip2Bone === bone) throwError('ID_TAKEN', { id: bone }, { path: 'grip2BoneId' });
    return { ...socket, grip2Bone };
  },
});

/** Longest socket name: `<name>-grip2` must stay within the 64-character name limit (CR-001, W2-31). */
const MAX_NAME = 58;

/** socket.create: name and parent checks, the factory insert, then the generated socket (and grip-2) bones. */
export const command = defineCommand({
  ...create,
  apply(draft, input, ctx) {
    if (input.name.length > MAX_NAME) {
      throwError('INVALID_NAME', { name: input.name, kind: 'socket' }, { path: 'name', hint: `socket names are at most ${MAX_NAME} characters so "<name>-grip2" fits in 64` });
    }
    if (draft.bones[ctx.resolve('bone', input.parentBone)]?.kind !== undefined) {
      throwError('VALIDATION', undefined, { path: 'parentBone', hint: 'sockets cannot sit on generated socket bones' });
    }
    create.apply(draft, input, ctx);
    const socket = draft.sockets[ctx.created[input.name] ?? ''];
    if (socket) insertSocketBones(draft, ctx, socket);
  },
});
